import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer, Cell,
} from "recharts";
import type { ForecastEmployeeRow, ForecastGroupRef, ForecastCell, SelfAllocStatus } from "@/features/finances/types/groupForecast";

const MONTHS_SHORT = ["Sty", "Lut", "Mar", "Kwi", "Maj", "Cze", "Lip", "Sie", "Wrz", "Paź", "Lis", "Gru"];
const FREE_KEY = "__free";
const NONPROJ_KEY = "__nonproj";
export const FREE_COLOR = "#9ca3af";      // szare = wolne moce
export const NONPROJ_COLOR = "#1f2937";   // czarne = miesiąc nieprojektowy
export const ALLOC_COLOR = "#6366f1";     // domyślny kolor przydziału (megawykres)

const sanitize = (c: string) => c.replace(/[^a-zA-Z0-9]/g, "");
// zakreskowanie = przydział zadeklarowany przez pracownika (self-allocation), nieedytowalny
const isLocked = (s: SelfAllocStatus) => s === "draft" || s === "submitted" || s === "approved";
const statusHint: Record<SelfAllocStatus, string | null> = {
  none: null, draft: "szkic", submitted: "złożona", approved: "zatw.",
};

/**
 * Skumulowany słupek obłożenia jednego człowieka: oś X = miesiące, oś Y = % etatu,
 * segmenty per grupa (kolory jak w siatce) + wolne moce. Linia 100% = pełne obłożenie.
 * Miesiące z przydziałem pracownika (self-allocation) są ZAKRESKOWANE i nieedytowalne;
 * miesiące bez deklaracji można kliknąć i edytować prognozę.
 */
export function OccupancyChart({ emp, groups, colorFor, onEditCell }: {
  emp: ForecastEmployeeRow;
  groups: ForecastGroupRef[];
  colorFor: (gid: string | null) => string;
  onEditCell?: (cell: ForecastCell) => void;
}) {
  const usedIds = new Set<string>();
  for (const cell of emp.by_month) {
    for (const seg of cell.segments) if (seg.group_id) usedIds.add(seg.group_id);
  }
  const usedGroups = groups.filter((g) => usedIds.has(g.id));
  const locked = emp.by_month.map((c) => isLocked(c.self_alloc_status));
  const anyLocked = locked.some(Boolean);
  const hasNonProj = emp.by_month.some((c) => c.is_non_project);
  const hasFree = emp.by_month.some(
    (c) => !c.is_non_project && c.free_fraction > 0.001,
  );

  const data: Record<string, number | string>[] = emp.by_month.map((cell) => {
    const row: Record<string, number | string> = {
      label: `${MONTHS_SHORT[cell.month - 1]} ${String(cell.year).slice(2)}`,
    };
    if (cell.is_non_project) {
      // miesiąc nieprojektowy → cały etat jako CZARNY (koszt rozkładany na zlecenia)
      row[NONPROJ_KEY] = 100;
    } else {
      for (const seg of cell.segments) {
        if (seg.group_id) row[seg.group_id] = Math.round(seg.fraction * 100);
      }
      if (cell.free_fraction > 0.001) {
        row[FREE_KEY] = Math.round(cell.free_fraction * 100);
      }
    }
    return row;
  });

  // Kolory użyte → potrzebne wzorce kreskowania (jeden pattern na kolor).
  const colorList = [...usedGroups.map((g) => colorFor(g.id)), FREE_COLOR];
  const fillFor = (color: string, i: number) => (locked[i] ? `url(#h-${sanitize(color)})` : color);

  // Klik w słupek miesiąca → edycja prognozy. Edytowalne tylko komórki z `editable`
  // (przyszłość zawsze; bieżący gdy pracownik nie złożył self-allocation).
  function clickMonth(i: number) {
    if (!onEditCell) return;
    if (!Number.isInteger(i) || i < 0 || i >= emp.by_month.length) return;
    const cell = emp.by_month[i];
    if (cell && cell.editable) onEditCell(cell);
  }

  // Custom tick: etykieta miesiąca + dopisek statusu (np. „szkic") pod spodem.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderTick = (props: any) => {
    const { x, y, payload } = props;
    const cell = emp.by_month[payload.index];
    const hint = cell ? statusHint[cell.self_alloc_status] : null;
    return (
      <g transform={`translate(${x},${y})`}>
        <text x={0} y={0} dy={10} textAnchor="middle" fontSize={10} fill="#6b7280">{payload.value}</text>
        {hint && (
          <text x={0} y={0} dy={20} textAnchor="middle" fontSize={8} fill="#7c3aed">{hint}</text>
        )}
      </g>
    );
  };

  return (
    <div>
    <ResponsiveContainer width="100%" height={anyLocked ? 168 : 150}>
      <BarChart
        data={data}
        margin={{ top: 8, right: 8, left: -22, bottom: anyLocked ? 14 : 0 }}
        className={onEditCell ? "cursor-pointer" : undefined}
        onClick={(state) => {
          const raw = state?.activeTooltipIndex;
          clickMonth(typeof raw === "number" ? raw : Number(raw));
        }}
      >
        <defs>
          {colorList.map((c) => (
            <pattern key={c} id={`h-${sanitize(c)}`} patternUnits="userSpaceOnUse" width={6} height={6} patternTransform="rotate(45)">
              <rect width={6} height={6} fill={c} />
              <line x1={0} y1={0} x2={0} y2={6} stroke="rgba(255,255,255,0.6)" strokeWidth={2.2} />
            </pattern>
          ))}
        </defs>
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" opacity={0.4} />
        <XAxis dataKey="label" tick={anyLocked ? renderTick : { fontSize: 10 }} interval={0} tickLine={false} />
        <YAxis domain={[0, 120]} ticks={[0, 50, 100]} unit="%" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
        <Tooltip
          contentStyle={{ fontSize: 12, borderRadius: 8 }}
          formatter={(value, name) => [`${value}%`, name === FREE_KEY ? "Wolne moce" : name === NONPROJ_KEY ? "Nieprojektowy" : name]}
          labelFormatter={(label, items) => {
            const idx = items?.[0]?.payload != null ? data.indexOf(items[0].payload) : -1;
            const st = idx >= 0 ? emp.by_month[idx]?.self_alloc_status : "none";
            const tag = st === "approved" ? " — przydział zatwierdzony" : st === "submitted" ? " — przydział złożony" : st === "draft" ? " — szkic przydziału" : "";
            return `${label}${tag}`;
          }}
        />
        <ReferenceLine y={100} stroke="#16a34a" strokeDasharray="4 4" />
        {usedGroups.map((g) => {
          const color = colorFor(g.id);
          return (
            <Bar
              key={g.id}
              dataKey={g.id}
              stackId="a"
              name={g.short_name || g.name}
              onClick={(_d, index) => clickMonth(Number(index))}
              cursor={onEditCell ? "pointer" : undefined}
            >
              {data.map((_row, i) => <Cell key={i} fill={fillFor(color, i)} />)}
            </Bar>
          );
        })}
        <Bar
          dataKey={FREE_KEY}
          stackId="a"
          name="Wolne moce"
          radius={[3, 3, 0, 0]}
          onClick={(_d, index) => clickMonth(Number(index))}
          cursor={onEditCell ? "pointer" : undefined}
        >
          {data.map((_row, i) => <Cell key={i} fill={fillFor(FREE_COLOR, i)} />)}
        </Bar>
        {/* czarne (nieprojektowy) zawsze na samej górze stosu */}
        <Bar
          dataKey={NONPROJ_KEY}
          stackId="a"
          name="Nieprojektowy"
          fill={NONPROJ_COLOR}
          radius={[3, 3, 0, 0]}
          onClick={(_d, index) => clickMonth(Number(index))}
          cursor={onEditCell ? "pointer" : undefined}
        />
      </BarChart>
    </ResponsiveContainer>
      {(usedGroups.length > 0 || hasFree || hasNonProj) && (
        <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 px-1">
          {usedGroups.map((g) => (
            <span
              key={g.id}
              className="flex items-center gap-1 text-[10px] text-gray-500 dark:text-gray-400"
            >
              <span
                className="w-2.5 h-2.5 rounded-sm flex-shrink-0"
                style={{ background: colorFor(g.id) }}
              />
              {g.short_name ? `${g.short_name} — ${g.name}` : g.name}
            </span>
          ))}
          {hasFree && (
            <span className="flex items-center gap-1 text-[10px] text-gray-500 dark:text-gray-400">
              <span
                className="w-2.5 h-2.5 rounded-sm flex-shrink-0"
                style={{ background: FREE_COLOR }}
              />
              Wolne moce
            </span>
          )}
          {hasNonProj && (
            <span className="flex items-center gap-1 text-[10px] text-gray-500 dark:text-gray-400">
              <span
                className="w-2.5 h-2.5 rounded-sm flex-shrink-0"
                style={{ background: NONPROJ_COLOR }}
              />
              Nieprojektowy
            </span>
          )}
        </div>
      )}
    </div>
  );
}
